import { z } from 'zod';

export type FoundationRole = 'creator' | 'developer';
export type FoundationStatus = 'checking' | 'available' | 'full';

export interface FoundationProgram {
  role: FoundationRole;
  seatTarget: number;
  seatUsed: number;
  state: 'inactive' | 'prelaunch' | 'active' | 'sold_out' | 'closed';
}

export interface FoundationState {
  status: FoundationStatus;
  spotsRemaining: number | null;
  totalSpots: number;
  loading: boolean;
  error: string | null;
}

// Validation schemas
export const creatorSignupSchema = z.object({
  email: z.string().email('Valid email required'),
  first_name: z.string().min(1, 'First name required').max(100),
  last_name: z.string().min(1, 'Last name required').max(100),
  phone: z.string().max(20).optional(),
  location: z.string().max(100).optional(),
  platforms: z.array(z.string()).optional(),
  content_types: z.string().max(500).optional(),
  audience_size: z.string().max(20).optional(),
  automation_interests: z.array(z.string()).optional(),
  current_challenges: z.string().max(1000).optional(),
});

export const developerSignupSchema = z.object({
  email: z.string().email('Valid email required'),
  first_name: z.string().min(1, 'First name required').max(100),
  last_name: z.string().min(1, 'Last name required').max(100),
  phone: z.string().max(20).optional(),
  location: z.string().max(100).optional(),
  experience_level: z.enum(['junior', 'mid', 'senior', 'lead']),
  primary_skills: z.array(z.string()).min(1, 'Select at least one skill'),
  github_url: z.string().url().optional().or(z.literal('')),
  linkedin_url: z.string().url().optional().or(z.literal('')),
  portfolio_url: z.string().url().optional().or(z.literal('')),
  motivation: z.string().min(10, 'Tell us why you want to join').max(2000),
});

export type CreatorSignupData = z.infer<typeof creatorSignupSchema>;
export type DeveloperSignupData = z.infer<typeof developerSignupSchema>;

export interface SignupResult {
  success: boolean;
  message?: string;
  seatNumber?: number;
  spotsRemaining?: number;
  errorCode?: 'DUPLICATE_EMAIL' | 'NO_SEATS' | 'RATE_LIMIT' | 'VALIDATION' | 'SERVER_ERROR';
}

// Program constants
export const FOUNDATION_CAPS = {
  creator: 1000,
  developer: 200,
} as const;

export const FOUNDATION_PERKS = {
  creator: [
    { icon: 'crown', label: 'Founding Creator Badge', value: 'Permanent' },
    { icon: 'percent', label: 'Early Pricing', value: '20% off forever' },
    { icon: 'zap', label: 'Free Credits', value: '100 credits' },
    { icon: 'rocket', label: 'Priority Access', value: '2 weeks early' },
  ],
  developer: [
    { icon: 'code', label: 'Founding Developer Badge', value: 'Permanent' },
    { icon: 'dollar', label: 'Better Take Rate', value: '25% higher' },
    { icon: 'zap', label: 'Free Credits', value: '200 credits' },
    { icon: 'star', label: 'Priority Publishing', value: 'Featured spots' },
  ],
} as const;
